/**
 * Zod schemas for the JSON data feeds the app actually consumes.
 *
 * These mirror the shape of the Opta-derived JSON files served from S3
 * (and bundled into `src/statics/data/{year}/`) for the four endpoints
 * fetched in `./index.js`:
 *
 *   - matches.json                      → MatchesSchema
 *   - squads/{teamId}.json              → SquadSchema
 *   - match-commentary/{matchId}.json   → CommentarySchema
 *   - match-stats/{matchId}.json        → MatchStatsSchema
 *
 * The schemas intentionally:
 *   • Use `.passthrough()` / `.loose()` so unknown keys are kept (the
 *     Opta payloads are large and we don't want validation to fail just
 *     because Stats Perform adds a new field).
 *   • Mark all but the truly-essential fields as `.optional()` so the
 *     app stays functional with partial data (pre-tournament, in-progress
 *     match, abandoned fixtures, etc.).
 *   • Coerce the obvious string/number ambiguity (Opta sends most numerics
 *     as strings) only where downstream code already relies on a specific
 *     type.
 *
 * Inferred types are exported for use elsewhere in the codebase.
 */

import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/*  Shared sub-schemas                                                         */
/* -------------------------------------------------------------------------- */

/** A `{ id, name }` reference (sport, ruleset, country, competition…) */
const RefSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
  })
  .loose();

/**
 * A contestant in a match.
 *
 * NOTE: For knockout fixtures whose teams aren't yet decided, Opta returns
 * a placeholder with only `{ phrase, position }` (e.g. "Winner Semi-final 1")
 * — no `id` or `name`. So every identity field is optional.
 */
const ContestantSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().optional(),
    shortName: z.string().optional(),
    officialName: z.string().optional(),
    code: z.string().optional(),
    position: z.enum(['home', 'away']).optional(),
    country: RefSchema.optional(),
    // Placeholder description for unresolved knockout slots
    // (e.g. "Winner Semi-final 1", "Loser Quarter-final 2")
    phrase: z.string().optional(),
  })
  .loose();

const VenueRefSchema = z
  .object({
    id: z.string(),
    neutral: z.string().optional(),
    longName: z.string().optional(),
    shortName: z.string().optional(),
    latitude: z.string().optional(),
    longitude: z.string().optional(),
  })
  .loose();

const ScoresSchema = z
  .object({
    home: z.number(),
    away: z.number(),
  })
  .loose();

/* -------------------------------------------------------------------------- */
/*  matches.json                                                               */
/* -------------------------------------------------------------------------- */

const MatchInfoSchema = z
  .object({
    id: z.string(),
    date: z.string().optional(),
    time: z.string().optional(),
    localDate: z.string().optional(),
    localTime: z.string().optional(),
    description: z.string().optional(),
    sport: RefSchema.optional(),
    ruleset: RefSchema.optional(),
    competition: RefSchema.loose().optional(),
    tournamentCalendar: z
      .object({
        id: z.string(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        name: z.string().optional(),
      })
      .loose()
      .optional(),
    stage: z
      .object({
        id: z.string().optional(),
        formatId: z.string().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        name: z.string().optional(),
      })
      .loose()
      .optional(),
    contestant: z.array(ContestantSchema).optional(),
    venue: VenueRefSchema.optional(),
  })
  .loose();

const PeriodSchema = z
  .object({
    id: z.number(),
    start: z.string().optional(),
    end: z.string().optional(),
    lengthMin: z.number().optional(),
    lengthSec: z.number().optional(),
  })
  .loose();

const GoalSchema = z
  .object({
    contestantId: z.string(),
    periodId: z.number().optional(),
    timeMin: z.number().optional(),
    timeMinSec: z.string().optional(),
    type: z.string().optional(),
    scorerId: z.string().optional(),
    scorerName: z.string().optional(),
    assistPlayerId: z.string().optional(),
    assistPlayerName: z.string().optional(),
    homeScore: z.number().optional(),
    awayScore: z.number().optional(),
  })
  .loose();

const CardSchema = z
  .object({
    contestantId: z.string(),
    periodId: z.number().optional(),
    timeMin: z.number().optional(),
    timeMinSec: z.string().optional(),
    type: z.string().optional(),
    playerId: z.string().optional(),
    playerName: z.string().optional(),
    cardReason: z.string().optional(),
  })
  .loose();

const SubstituteSchema = z
  .object({
    contestantId: z.string(),
    periodId: z.number().optional(),
    timeMin: z.number().optional(),
    timeMinSec: z.string().optional(),
    playerOnId: z.string().optional(),
    playerOnName: z.string().optional(),
    playerOffId: z.string().optional(),
    playerOffName: z.string().optional(),
    subReason: z.string().optional(),
  })
  .loose();

const MatchOfficialSchema = z
  .object({
    id: z.string().optional(),
    type: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
  })
  .loose();

const MatchDetailsSchema = z
  .object({
    periodId: z.number().optional(),
    matchStatus: z.string().optional(),
    winner: z.string().optional(),
    matchLengthMin: z.number().optional(),
    matchLengthSec: z.number().optional(),
    period: z.array(PeriodSchema).optional(),
    scores: z
      .object({
        ht: ScoresSchema.optional(),
        ft: ScoresSchema.optional(),
        et: ScoresSchema.optional(),
        pen: ScoresSchema.optional(),
        total: ScoresSchema.optional(),
      })
      .loose()
      .optional(),
  })
  .loose();

const LiveDataSchema = z
  .object({
    matchDetails: MatchDetailsSchema.optional(),
    goal: z.array(GoalSchema).optional(),
    card: z.array(CardSchema).optional(),
    substitute: z.array(SubstituteSchema).optional(),
    matchDetailsExtra: z
      .object({
        attendance: z.string().optional(),
        matchOfficial: z.array(MatchOfficialSchema).optional(),
      })
      .loose()
      .optional(),
  })
  .loose();

/** A single fixture inside `matches.matches.match[]` */
export const MatchSchema = z
  .object({
    matchInfo: MatchInfoSchema,
    liveData: LiveDataSchema.optional(),
  })
  .loose();

const StandingsRankingSchema = z
  .object({
    rank: z.number().optional(),
    rankStatus: z.string().optional(),
    rankId: z.string().optional(),
    lastRank: z.number().optional(),
    contestantId: z.string(),
    contestantName: z.string().optional(),
    contestantShortName: z.string().optional(),
    contestantClubName: z.string().optional(),
    contestantCode: z.string().optional(),
    points: z.number().optional(),
    matchesPlayed: z.number().optional(),
    matchesWon: z.number().optional(),
    matchesLost: z.number().optional(),
    matchesDrawn: z.number().optional(),
    goalsFor: z.number().optional(),
    goalsAgainst: z.number().optional(),
    goaldifference: z.string().optional(),
    lastSix: z.string().optional(),
  })
  .loose();

const StandingsDivisionSchema = z
  .object({
    groupId: z.string().optional(),
    groupName: z.string().optional(),
    type: z.string().optional(),
    ranking: z.array(StandingsRankingSchema).optional(),
  })
  .loose();

const StandingsStageSchema = z
  .object({
    id: z.string().optional(),
    formatId: z.string().optional(),
    name: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    division: z.array(StandingsDivisionSchema).optional(),
  })
  .loose();

const VenueDetailSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    city: z.string().optional(),
    countryId: z.string().optional(),
    country: z.string().optional(),
    countryCode: z.string().optional(),
    address: z.string().optional(),
    opened: z.number().optional(),
    capacity: z.number().optional(),
    surface: z.string().optional(),
    mapsGeoCodeLatitude: z.string().optional(),
    mapsGeoCodeLongitude: z.string().optional(),
    timeZone: z.string().optional(),
  })
  .loose();

/** Full payload of `matches.json` */
export const MatchesSchema = z
  .object({
    matches: z
      .object({
        match: z.array(MatchSchema),
      })
      .loose(),
    standings: z
      .object({
        stage: z.array(StandingsStageSchema).optional(),
      })
      .loose()
      .optional(),
    venues: z
      .object({
        venue: z.array(VenueDetailSchema).optional(),
      })
      .loose()
      .optional(),
  })
  .loose();

/* -------------------------------------------------------------------------- */
/*  squads/{teamId}.json                                                       */
/* -------------------------------------------------------------------------- */

const PlayerStatsBlockSchema = z
  .object({
    goals: z.number().optional(),
    assists: z.number().optional(),
    penaltyGoals: z.number().optional(),
    appearances: z.number().optional(),
    yellowCards: z.number().optional(),
    secondYellowCards: z.number().optional(),
    redCards: z.number().optional(),
    minutesPlayed: z.number().optional(),
  })
  .loose();

export const SquadPlayerSchema = z
  .object({
    id: z.string(),
    team: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    matchName: z.string().optional(),
    position: z.string().optional(),
    shirtNumber: z.number().optional(),
    role: z.string().optional(),
    stats: z
      .object({
        national: PlayerStatsBlockSchema.optional(),
        club: PlayerStatsBlockSchema.optional(),
      })
      .loose()
      .optional(),
  })
  .loose();

/** A squad file is a flat array of players (incl. manager rows) */
export const SquadSchema = z.array(SquadPlayerSchema);

/* -------------------------------------------------------------------------- */
/*  match-commentary/{matchId}.json                                            */
/* -------------------------------------------------------------------------- */

export const CommentaryItemSchema = z
  .object({
    id: z.string().optional(),
    comment: z.string().optional(),
    lastModified: z.string().optional(),
    minute: z.string().optional(),
    period: z.string().optional(),
    second: z.string().optional(),
    time: z.string().optional(),
    /**
     * Known values: 'comment', 'goal', 'assist', 'yellow card', 'red card',
     * 'substitution', 'highlight', 'team news', 'half_time summary',
     * 'post_match summary', etc. Left as a free-form string so a new card
     * type from the feed never breaks validation.
     */
    type: z.string().optional(),
  })
  .loose();

export const CommentarySchema = z.array(CommentaryItemSchema);

/* -------------------------------------------------------------------------- */
/*  match-stats/{matchId}.json                                                 */
/* -------------------------------------------------------------------------- */

const StatEntrySchema = z
  .object({
    type: z.string(),
    value: z.string().optional(),
    fh: z.string().optional(),
    sh: z.string().optional(),
  })
  .loose();

const PlayerMatchStatsSchema = z
  .object({
    playerId: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    matchName: z.string().optional(),
    shirtNumber: z.number().optional(),
    position: z.string().optional(),
    positionSide: z.string().optional(),
    formationPlace: z.string().optional(),
    stat: z.array(StatEntrySchema).optional(),
  })
  .loose();

const TeamOfficialSchema = z
  .object({
    id: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    type: z.string().optional(),
  })
  .loose();

const KitSchema = z
  .object({
    id: z.string().optional(),
    colour1: z.string().optional(),
    type: z.string().optional(),
  })
  .loose();

export const TeamMatchStatsSchema = z
  .object({
    contestantId: z.string(),
    formationUsed: z.string().optional(),
    kit: KitSchema.optional(),
    player: z.array(PlayerMatchStatsSchema).optional(),
    stat: z.array(StatEntrySchema).optional(),
    teamOfficial: z.array(TeamOfficialSchema).optional(),
  })
  .loose();

/** A match-stats file is a tuple of [homeTeamStats, awayTeamStats] */
export const MatchStatsSchema = z.array(TeamMatchStatsSchema);

/* -------------------------------------------------------------------------- */
/*  Helper                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Run a Zod schema against arbitrary data. On failure, log a structured
 * warning (only in dev) and return the original payload so the app keeps
 * functioning. On success, return the parsed (and stripped-where-defined)
 * payload.
 *
 * Use this from `data/index.js` like so:
 *
 *     const json = await safeFetchJson(...);
 *     return validate(MatchesSchema, json, 'matches.json');
 *
 * @template T
 * @param {import('zod').ZodType<T>} schema
 * @param {unknown} data
 * @param {string} label  A short label for log messages (the URL or filename).
 * @returns {T}
 */
export function validate(schema, data, label) {
  if (data == null) return /** @type {T} */ (data);
  const result = schema.safeParse(data);
  if (result.success) return result.data;

  // Only log in dev — in prod we don't want the console flooded.
  if (typeof window !== 'undefined' && import.meta?.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[data] Schema validation failed for ${label}:`,
      result.error.issues.slice(0, 5)
    );
  } else if (
    typeof process !== 'undefined' &&
    process.env?.NODE_ENV !== 'production'
  ) {
    // SSR / Node side
    // eslint-disable-next-line no-console
    console.warn(
      `[data] Schema validation failed for ${label}:`,
      result.error.issues.slice(0, 5)
    );
  }

  // Be permissive: hand the (untouched) raw payload back so downstream
  // code can decide how to cope.
  return /** @type {T} */ (data);
}
